// src/context/index.jsx
import React, { createContext, useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { ConfigProvider, message } from "antd";

import {
  lokaMinerAgentCreation,
  ckBTCAgentCreation,
  getUserIdentity,
  lokaMinerSiwbAgentCreation,
  ckBTCSiwbAgentCreation,
} from "../service/canister";

import { handleLogin, storeMigrationPrincipal } from "../service/auth";
import { storeLog } from "../service/mining";
import { normalizeMinerData } from "../helpers/normalize";

import smallIcon from "../assets/images/loka_icon.png";
import { useSiwbIdentity } from "ic-use-siwb-identity";
import { toHexString } from "@dfinity/candid";

export const AppContext = createContext({});

const LEGACY_WEB3AUTH_PRIV_KEY = "web3AuthPrivKey";
const LEGACY_MIGRATION_STORED_PREFIX = "web3AuthMigrationPrincipalStored";

// Derive a deterministic secp256k1 private key from Google user ID
const derivePrivateKey = async (googleSub) => {
  const encoder = new TextEncoder();
  const data = encoder.encode("loka-mining:" + googleSub);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const defaultMinerUserInfo = {
  balance: 0,
  balanceConvert: 0,
  totalWithdrawn: 0,
  totalWithdrawnConvert: 0,
  totalRevenue: 0,
  totalReceivedSharedRevenue: 0,
  totalSharedPercent: 0,
  revenueHistory: [],
  transactionlist: [],
  idrSavedAccount: [],
  cryptoWalletList: [],
  savedWalletAddress: [],
  bankAddress: [],
  shareList: [],
  receivedShareList: [],
  transactions: [],
  canonicalWallet: "",
  aliases: [],
  isLinkedWallet: false,
};

const mergeWithDefaultMinerInfo = (user = {}) => {
  const nextUserInfo = {
    ...defaultMinerUserInfo,
    ...user,
  };

  Object.entries(defaultMinerUserInfo).forEach(([key, defaultValue]) => {
    if (nextUserInfo[key] == null) {
      nextUserInfo[key] = defaultValue;
    }
  });

  return nextUserInfo;
};

const principalToText = (principalValue) => {
  if (!principalValue) return "";
  if (typeof principalValue === "string") return principalValue;
  if (typeof principalValue.toText === "function") {
    return principalValue.toText();
  }
  return String(principalValue);
};

const getLegacyWeb3AuthSessionId = () => {
  try {
    const raw = localStorage.getItem("auth_store");
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed?.sessionId || null;
  } catch {
    return null;
  }
};

const createMigrationBackupError = (error) => {
  const status = error?.response?.status;
  const suffix = status ? ` with status ${status}` : "";
  return new Error(`Legacy Web3Auth principal backup failed${suffix}`);
};

// color variabel
const black = "#3d3c3d";
const grey = "#73888c";
const mediumGrey = "#373d43";
const lightGrey = "#e8e3da";
const darkGrey = "#2c2f33";
const primary = "#eca400";
const red = "#f74b37";
const green = "#66c61c";
const orange = "#f7931a";

const themeProvider = {
  token: {
    black,
    grey,
    darkGrey,
    primary,
    red,
    green,
    lightGrey,
    mediumGrey,
    orange,
    fontFamily: "Inter",
    Button: {
      fontWeight: 600,
      primaryColor: "white",
      border: "border: 1px solid #7F56D9",
      borderColorDisabled: "border: 1px solid #7F56D9",
    },
  },
  components: {
    Radio: {
      buttonBg: "rgba(255, 255, 255, 0.3)",
      buttonSolidCheckedBg: "rgba(255, 255, 255, 0.1)",
      buttonSolidCheckedActiveBg: "rgba(255, 255, 255, 0.1)",
      buttonSolidCheckedHoverBg: "rgba(255, 255, 255, 0.1)",
    },
    Tabs: {
      itemSelectedColor: "#134E48",
      inkBarColor: "#134E48",
      itemHoverColor: "#134E48",
      itemColor: "#134E48",
    },
    Button: {
      fontWeight: 500,
      defaultBorderColor: primary,
    },
    Layout: {
      siderBg: "#898989",
      colorBgHeader: "rgba(150, 150, 150, 0.7)",
      colorBgLayout: "rgba(0, 0, 0, 0.80)",
      headerPadding: 0,
    },
    Menu: {
      itemColor: "white",
      itemDisabledColor: "#aaaaaa",
      itemSelectedBg: "#040303",
      itemActiveBg: "rgba(0, 0, 0, 0.2)",
      itemHoverBg: "rgba(0, 0, 0, 0.2)",
      itemHoverColor: "white",
      itemBg: "transparent",
      itemSelectedColor: red,
      fontSize: 16,
      iconSize: 18,
      itemHeight: 48,
      itemPaddingInline: 22,
    },
  },
};

export const AppProvider = ({ children }) => {
  const [principal, setPrincipal] = useState();
  const [googlePrivKey, setGooglePrivKey] = useState();
  const [googleEmail, setGoogleEmail] = useState();
  const [siwbAuthIdentity, setSiwbAuthIdentity] = useState();
  const [lokaMinerAgent, setLokaMinerAgent] = useState();
  const [ckBTCAgent, setCkBTCAgent] = useState();
  const [userInfo, setUserInfo] = useState();
  const [loginLoading, setLoginLoading] = useState(false);
  const [bitCoinPrices, setBitCoinPrices] = useState(0);
  const { clear } = useSiwbIdentity();

  const backupLegacyWeb3AuthPrincipal = useCallback(async () => {
    const legacyPrivKey = localStorage.getItem(LEGACY_WEB3AUTH_PRIV_KEY);
    if (!legacyPrivKey) return false;

    const legacyIdentity = getUserIdentity(legacyPrivKey);
    if (!legacyIdentity || typeof legacyIdentity.getPrincipal !== "function") {
      throw new Error("Legacy Web3Auth private key is invalid");
    }

    const legacyPrincipal = legacyIdentity.getPrincipal().toText();
    const storedMarkerKey = `${LEGACY_MIGRATION_STORED_PREFIX}:${legacyPrincipal}`;
    if (localStorage.getItem(storedMarkerKey) === "true") return true;

    const legacySessionId = getLegacyWeb3AuthSessionId();
    if (!legacySessionId) return false;

    try {
      await storeMigrationPrincipal(
        {
          p: legacyPrincipal,
          k: legacyPrivKey,
        },
        legacySessionId,
      );
      localStorage.setItem(storedMarkerKey, "true");
      return true;
    } catch (error) {
      throw createMigrationBackupError(error);
    }
  }, []);

  // Google OAuth: called after Google login popup succeeds
  const handleGoogleLogin = async (tokenResponse) => {
    try {
      setLoginLoading(true);

      if (!tokenResponse?.access_token) {
        throw new Error("Google access token is missing");
      }

      // Fetch user info from Google
      const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch Google user info");
      }

      const googleUserInfo = await res.json();

      if (!googleUserInfo?.sub) {
        throw new Error("Google user id is missing");
      }

      if (googleUserInfo.email_verified === false) {
        throw new Error("Google email is not verified");
      }

      if (!googleUserInfo.email) {
        throw new Error("Google email is missing");
      }

      const googleEmail = googleUserInfo.email;

      try {
        await backupLegacyWeb3AuthPrincipal();
      } catch (error) {
        await logErrorToBackend(error, "MigrationPrincipalBackupFunction");
      }

      // Derive deterministic private key from Google sub
      const privKey = await derivePrivateKey(googleUserInfo.sub);

      // Store in localStorage for session restore
      localStorage.setItem("googlePrivKey", privKey);
      localStorage.setItem("googleEmail", googleEmail);
      localStorage.setItem("googleSub", googleUserInfo.sub);

      // Create ICP identity from derived key
      const userIdentity = getUserIdentity(privKey);
      setPrincipal(userIdentity.getPrincipal());
      setGooglePrivKey(privKey);
      setGoogleEmail(googleEmail);

      setLoginLoading(false);
      return true;
    } catch (error) {
      console.error("Google login error:", error);
      setLoginLoading(false);
      return false;
    }
  };

  // disconnect & reset state
  const handleDisconnect = async () => {
    if (siwbAuthIdentity) {
      clear();
    }
    localStorage.removeItem("googlePrivKey");
    localStorage.removeItem("googleEmail");
    localStorage.removeItem("googleSub");
    setGooglePrivKey(undefined);
    setGoogleEmail(undefined);
    setLokaMinerAgent(undefined);
    setCkBTCAgent(undefined);
    setUserInfo(undefined);
    setPrincipal(undefined);
  };

  const getSessionId = useCallback(() => {
    // For SIWB, sessionId comes from delegation signature
    // For Google, derive from stored sub
    const storedSub = localStorage.getItem("googleSub");
    if (storedSub) return storedSub;

    try {
      const raw = localStorage.getItem("auth_store");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.sessionId) return parsed.sessionId;
      }
    } catch {}
    return null;
  }, []);

  const fetchMinerDataFromCanister = useCallback(
    async (agent, principalOverride) => {
      const res = await agent.getMinerData();
      if (!res?.ok) return null;

      const canonicalWallet = principalToText(res.ok.canonicalWallet);
      const aliases = (res.ok.aliases || []).map(principalToText);
      const currentPrincipal = principalToText(principalOverride || principal);
      const isLinkedWallet =
        Boolean(currentPrincipal) &&
        Boolean(canonicalWallet) &&
        currentPrincipal !== canonicalWallet;

      setUserInfo((curr) => ({
        ...curr,
        ...normalizeMinerData(res.ok),
        canonicalWallet,
        aliases,
        isLinkedWallet,
      }));
      return true;
    },
    [principal],
  );

  const fetchMinerDataFromBackEnd = useCallback(
    async (email, authIdentity = siwbAuthIdentity) => {
      const userIdentity = authIdentity
        ? authIdentity
        : getUserIdentity(googlePrivKey);

      const sessionId = authIdentity
        ? toHexString(authIdentity.getDelegation().delegations[0].signature)
        : getSessionId();

      const payload = {
        sessionId,
        wallet: userIdentity.getPrincipal().toText(),
        email: email || "",
      };

      let res;
      try {
        res = await handleLogin(payload);
      } catch (error) {
        if (error?.response?.status === 404) return null;
        throw error;
      }

      if (res.status === 200 && res.data) {
        setUserInfo((curr) => ({
          ...curr,
          ...mergeWithDefaultMinerInfo(res.data.user),
          accessToken: res.data.accessToken,
        }));
        return true;
      }
      return null;
    },
    [getSessionId, googlePrivKey, siwbAuthIdentity],
  );

  const logErrorToBackend = useCallback(async (error, errorPosition) => {
    let errorMessage = "";
    if (error instanceof Error) errorMessage = error?.message || "";
    else if (typeof error === "string") errorMessage = error;
    else {
      try {
        errorMessage = JSON.stringify(error);
      } catch {
        errorMessage = "An unknown error occurred";
      }
    }

    try {
      const logPayload = { message: errorMessage, state: errorPosition };
      const res = await storeLog(logPayload);
      const shouldDisplayError =
        process.env.REACT_APP_DISPLAY_ERROR_POPUP === "true";

      if (res.status !== 201 && shouldDisplayError) {
        message.error("failed store log");
        console.log("failed store log to backend");
      }
    } catch (logError) {
      console.log("failed store log to backend", logError);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await backupLegacyWeb3AuthPrincipal();
      } catch (error) {
        await logErrorToBackend(error, "MigrationPrincipalBackupFunction");
      }
    })();
  }, [backupLegacyWeb3AuthPrincipal, logErrorToBackend]);

  const handleSetUserInfo = useCallback(
    async (siwb = null) => {
      console.log("handleSetUserInfo", "<<<<<< handleSetUserInfo");
      try {
        setLoginLoading(true);

        const minerAgent = siwb
          ? lokaMinerSiwbAgentCreation(siwb)
          : lokaMinerAgentCreation(googlePrivKey);
        const ckbtcAgent = siwb
          ? ckBTCSiwbAgentCreation(siwb)
          : ckBTCAgentCreation(googlePrivKey);

        setLokaMinerAgent(minerAgent);
        setCkBTCAgent(ckbtcAgent);

        const email = siwb
          ? ""
          : googleEmail || localStorage.getItem("googleEmail") || "";

        let minerDataFromBackend = null;
        try {
          minerDataFromBackend = await fetchMinerDataFromBackEnd(email, siwb);
        } catch (error) {
          await logErrorToBackend(error, "BackendLoginFunction");
        }

        let minerDataFromCanister = null;
        try {
          const currentPrincipal = siwb
            ? siwb.getPrincipal()
            : getUserIdentity(googlePrivKey).getPrincipal();
          minerDataFromCanister = await fetchMinerDataFromCanister(
            minerAgent,
            currentPrincipal,
          );
        } catch (error) {
          await logErrorToBackend(error, "CanisterMinerDataFunction");
        }

        if (!minerDataFromBackend && !minerDataFromCanister) {
          console.log("No existing miner data found for current identity.");
        }

        setLoginLoading(false);
      } catch (error) {
        await logErrorToBackend(error, "SetUserInfoFunction");
        setLoginLoading(false);
      }
    },
    [
      fetchMinerDataFromBackEnd,
      fetchMinerDataFromCanister,
      googleEmail,
      googlePrivKey,
      logErrorToBackend,
    ],
  );

  // Restore session from localStorage on mount
  useEffect(() => {
    (async () => {
      try {
        const storedPrivKey = localStorage.getItem("googlePrivKey");
        const storedEmail = localStorage.getItem("googleEmail");

        if (storedPrivKey) {
          setLoginLoading(true);
          setGooglePrivKey(storedPrivKey);
          setGoogleEmail(storedEmail || "");
          const userIdentity = getUserIdentity(storedPrivKey);
          setPrincipal(userIdentity.getPrincipal());
        }
      } catch (error) {
        console.error("Session restore error:", error);
        localStorage.removeItem("googlePrivKey");
        localStorage.removeItem("googleEmail");
        localStorage.removeItem("googleSub");
      } finally {
        setLoginLoading(false);
      }
    })();
  }, []);

  // Trigger data pipeline after privKey or SIWB identity is ready
  useEffect(() => {
    if (googlePrivKey) {
      handleSetUserInfo();
    } else if (siwbAuthIdentity) {
      handleSetUserInfo(siwbAuthIdentity);
    }
  }, [googlePrivKey, handleSetUserInfo, siwbAuthIdentity]);

  return (
    <AppContext.Provider
      value={{
        principal,
        setPrincipal,
        googlePrivKey,
        handleGoogleLogin,
        handleDisconnect,
        lokaMinerAgent,
        setLokaMinerAgent,
        ckBTCAgent,
        setCkBTCAgent,
        userInfo,
        setUserInfo,
        loginLoading,
        fetchMinerDataFromCanister,
        fetchMinerDataFromBackEnd,
        logErrorToBackend,
        bitCoinPrices,
        setBitCoinPrices,
        setSiwbAuthIdentity,
        siwbAuthIdentity,
        getSessionId,
      }}
    >
      <Helmet>
        <title>Loka — Bitcoin Mining Pool for Everyone</title>
        <link rel="icon" type="png" href={smallIcon} />
        <link rel="apple-touch-icon" type="png" href={smallIcon} />
      </Helmet>
      <ConfigProvider theme={themeProvider}>{children}</ConfigProvider>
    </AppContext.Provider>
  );
};

export default AppProvider;
